import type { GetProjectsQuery } from '@/src/common/api/graphql/getProjectsGql';
import type { ClaimEstimate } from '@common/api/graphql/getClaimEstimate';
import type {
  API_CONFIDENTIAL_INFORMATION,
  BULK_CONFIDENTIAL_INFORMATION,
  NULL_CONFIDENTIAL_INFORMATION,
  QueryAPIConfidentialInformationGiftCardConfig,
  QueryAPIConfidentialInformationGifteeBoxConfig,
  QueryAccessToken,
  QueryApiAllowedIps,
  QueryApiGiftCardIssueConfig,
  QueryBulkGifteeBoxIssueConfig,
  QueryBulkGifteeBoxIssueConfigIssueAmountRequest,
  QueryFixedIssueConfig,
  QueryGiftCardConfig,
  QueryGiftCardTicketConfig,
  QueryGiftUsageReviewStatusTypes,
  QueryGifteeBoxConfig,
  QueryHostCompany,
  QueryIssueCode,
  QueryMarketingIndustry,
  QueryMessageCardConfig,
  QueryMessageThread,
  QueryProjectAdvertisingMediumRegistration,
  QueryProjectClaims,
  QueryProjectGiftDistributionChannel,
  QueryProjectPurposeCategory,
  QueryProjectUrlCode,
  QuerySolutionSystem
} from '../../../common/api/graphql/getProjectsGql';
import type { Project } from '../../../common/api/graphql/projectTypes';
import { AppActionTypes } from '../actions/actionType';
import type { AppAction } from '../actions/actionType';

export type LocalProject = Project<
  QueryProjectGiftDistributionChannel[],
  LocalSolutionSystem,
  LocalGiftCardConfig[],
  QueryProjectPurposeCategory,
  QueryMarketingIndustry
> & {
  isSample: boolean;
  isAnyContentPartnerReviewRequested: boolean;
  isCancelled: boolean;
  messageThread: QueryMessageThread | null;
  urlCode: QueryProjectUrlCode;
  hostCompany: LocalHostCompany;
  claims: QueryProjectClaims;
  confidentialInformation: LocalConfidentialInformation;
  projectAdvertisingMediumRegistrations: QueryProjectAdvertisingMediumRegistration[];
  gifteeBoxConfigs: LocalGifteeBoxConfigs;
};

export type LocalSolutionSystem = Omit<
  QuerySolutionSystem,
  'solutionSystemIdentifier'
> & {
  solutionSystemIdentifier:
    | QuerySolutionSystem['solutionSystemIdentifier']
    | null;
};

export type LocalGifteeBoxConfigs = LocalGifteeBoxConfig[] | null;
export type LocalGifteeBoxConfig = Omit<
  QueryGifteeBoxConfig,
  'bulkGifteeBoxIssueConfigs'
> & {
  bulkGifteeBoxIssueConfigs: LocalBulkGifteeBoxIssueConfig[];
};

export type LocalBulkGifteeBoxIssueConfig = QueryBulkGifteeBoxIssueConfig & {
  isIssueAmountRequested: boolean;
  issueAmountRequest: LocalBulkGifteeBoxIssueConfigIssueAmountRequest;
  claimEstimate: LocalClaimEstimate;
};

export type LocalBulkGifteeBoxIssueConfigIssueAmountRequest =
  NonNullable<QueryBulkGifteeBoxIssueConfigIssueAmountRequest>;

export type LocalConfidentialInformation =
  | LocalNullConfidentialInformation
  | LocalBulkConfidentialInformation
  | LocalAPIConfidentialInformation;

export type LocalNullConfidentialInformation = {
  __typename: typeof NULL_CONFIDENTIAL_INFORMATION;
  reason: string;
};

type LocalBulkConfidentialInformation = {
  __typename: typeof BULK_CONFIDENTIAL_INFORMATION;
  bulkDeliverables?: LocalBulkDeliverable[];
};

export type LocalAPIConfidentialInformation = {
  __typename: typeof API_CONFIDENTIAL_INFORMATION;
  apiAllowedIps: QueryApiAllowedIps;
  accessTokens: QueryAccessToken[];
  giftCardConfigs: LocalAPIConfidentialInformationGiftCardConfigs;
  gifteeBoxConfigs: LocalAPIConfidentialInformationGifteeBoxConfigs;
};

export type LocalAPIConfidentialInformationGiftCardConfig =
  QueryAPIConfidentialInformationGiftCardConfig & {
    issueCode: LocalIssueCode;
  };

type LocalIssueCode = QueryIssueCode;

type LocalAPIConfidentialInformationGiftCardConfigs =
  LocalAPIConfidentialInformationGiftCardConfig[];

export type LocalAPIConfidentialInformationGifteeBoxConfig =
  QueryAPIConfidentialInformationGifteeBoxConfig & {
    issueCode: LocalIssueCode;
  };

type LocalAPIConfidentialInformationGifteeBoxConfigs =
  LocalAPIConfidentialInformationGifteeBoxConfig[];

type LocalIssueAmountRequest = {
  issueAmount: number;
  requestedAt: string;
};

type LocalHostCompany = QueryHostCompany;

export type LocalGiftCardConfig = {
  urlCode: string;
  unitPrice: number;
  giftWalletStorable: boolean;
  giftCardTicketConfigs: LocalGiftCardTicketConfig[];
  messageCardConfig: QueryMessageCardConfig | null;
  creativeCheckApproved: boolean;
  giftUsageReviewStatus: QueryGiftUsageReviewStatusTypes;
  isIssuable: boolean;
  bulkGiftCardIssueConfigs: LocalBulkGiftCardIssueConfig[];
  apiGiftCardIssueConfigs: LocalApiGiftCardIssueConfig[];
  currentApiGiftCardIssueConfig: LocalApiGiftCardIssueConfig | null;
};

export type LocalGiftCardTicketConfig = QueryGiftCardTicketConfig;

export type LocalClaimEstimate = null | ClaimEstimate;

export type LocalBulkGiftCardIssueConfig = {
  fixedIssueConfig: QueryFixedIssueConfig;
  deliveryDate: string;
  issueAmountRequest: LocalIssueAmountRequest;
  requestedIssueAmount: number;
  urlCode: string;
  isIssueAmountRequested: boolean;
  claimEstimate: LocalClaimEstimate;
};

export type LocalBulkDeliverable =
  | LocalBulkGiftCardDeliverable
  | LocalBulkGifteeBoxDeliverable;

type LocalBulkGiftCardDeliverable = {
  __typename: 'BulkGiftCardDeliverable';
  giftCardConfig: LocalBulkGiftCardDeliverableGiftCardConfig;
  unitPrice: number;
} & LocalBulkDeliverableBase;

type LocalBulkGifteeBoxDeliverable = {
  __typename: 'BulkGifteeBoxDeliverable';
  gifteeBoxConfig: LocalBulkGifteeBoxDeliverableGifteeBoxConfig;
  initialPoint: number;
  exchangeableEndLabel: string;
} & LocalBulkDeliverableBase;

type LocalBulkGiftCardDeliverableGiftCardConfig = QueryGiftCardConfig;

type LocalBulkGifteeBoxDeliverableGifteeBoxConfig = QueryGifteeBoxConfig;

type LocalBulkDeliverableBase = {
  urlCode: string;
  issueAmount: number;
  deliveredAt: string;
  downloadType: string;
};

export type LocalApiGiftCardIssueConfig = QueryApiGiftCardIssueConfig;

// ダッシュボード用に最小限の情報を持つ Project の State 型定義
export type ProjectsState = GetProjectsQuery['projects'];
export type ProjectState = ProjectsState[number];

const projects = (
  state: ProjectsState = [],
  action: AppAction
): ProjectsState => {
  switch (action.type) {
    case AppActionTypes.GET_PROJECTS_SUCCESS:
      return action.projects;
    case AppActionTypes.CREATE_SAMPLE_PROJECT_SUCCESS:
      return [action.project, ...state];
    default:
      return state;
  }
};

export default projects;
